import {
  FC,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
  memo,
  useState,
  useLayoutEffect,
  useEffect,
} from "react";
import {Box, CircularProgress, Paper, Typography} from "@mui/material";
import Draggable from "react-draggable";
import useDraggableChat from "../../hooks/useDraggableChat";
import styles from "./ChatDialog.module.scss";
import MinimizedChatIcon from "../MinimizedIcon/MinimizedChatIcon";
import {useLocation, useParams} from "react-router-dom";
const ChatHeader = lazy(() => import("../ChatHeader/ChatHeader"));
const ChatInput = lazy(() => import("../ChatInput/ChatInput"));
const ChatMessages = lazy(() => import("../ChatMessages/ChatMessages"));
const ChatWelcomeScreen = lazy(
  () => import("../ChatWelcomeScreen/ChatWelcomeScreen"),
);
const ChatPastSessions = lazy(
  () => import("../ChatPastSessions/ChatPastSessions"),
);

interface ChatDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChatDialog: FC<ChatDialogProps> = ({ open, onClose }) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    view,
    currentConversation,
    isMinimized,
    setIsMinimized,
    isInitializing,
    dragPosition,
    setDragPosition,
  } = useDraggableChat();

  const isFirstOpen = useRef(true);

  useEffect(() => {
    if (open && isFirstOpen.current) {
      setDragPosition({ x: 0, y: 0 });
      isFirstOpen.current = false;
    }
  }, [open, setDragPosition]);

  const messages = useMemo(
    () => currentConversation?.messages || [],
    [currentConversation],
  );
  const isLoading = useMemo(
    () => isInitializing || currentConversation?.loading,
    [isInitializing, currentConversation?.loading],
  );

  const [historyOnRight, setHistoryOnRight] = useState(false);
  const prevView = useRef(view);
  const prevIsMinimized = useRef(isMinimized);

  const params = useParams();
  const location = useLocation();
  const isJourneyPage = location.pathname.includes("journeys") && !!params.id;
  const [isBlocked, setIsBlocked] = useState(false);

  useLayoutEffect(() => {
    const historyWidthWithGap = 302;
    const mainChatWidth = 410;
    const padding = 20;

    // 1. Initial positioning when opening history
    if (prevView.current !== view) {
      if (view === "pastChats") {
        const containerLeft = 20 + dragPosition.x;
        if (containerLeft < historyWidthWithGap) {
          setHistoryOnRight(true);
        } else {
          setHistoryOnRight(false);
        }
      } else {
        setHistoryOnRight(false);
      }
      prevView.current = view;
    }

    // 2. Clamp position on Maximize (prevent right-side overflow)
    if (prevIsMinimized.current && !isMinimized) {
      const currentMarginLeft = (view === "pastChats" && !historyOnRight) ? -historyWidthWithGap : 0;
      const containerLeft = 20 + dragPosition.x + currentMarginLeft;
      const totalWidth = view === "pastChats" ? mainChatWidth + historyWidthWithGap : mainChatWidth;
      const overflow = (containerLeft + totalWidth + padding) - window.innerWidth;
      
      if (overflow > 0) {
        setDragPosition((prev) => ({ ...prev, x: prev.x - overflow }));
      }
    }
    prevIsMinimized.current = isMinimized;

    // 3. Flip history panel if it goes off-screen during drag
    if (view === "pastChats" && nodeRef.current) {
      const rect = nodeRef.current.getBoundingClientRect();
      if (!historyOnRight && rect.left < 5) {
        setHistoryOnRight(true);
      } else if (historyOnRight && rect.right > window.innerWidth - 5) {
        setHistoryOnRight(false);
      }
    }
  }, [view, isMinimized, dragPosition.x, historyOnRight, setDragPosition]);

  useEffect(() => {
    const checkFocus = () => {
        setTimeout(() => {
            const activeEl = document.activeElement;
            const allDialogs = document.querySelectorAll(
                '.MuiDialog-root, .MuiDrawer-root, [role="dialog"], .MuiPopover-root'
            );

            const externalDialogExists = Array.from(allDialogs).some(el => {
                const isChatMenu = el.querySelector('.MuiMenu-list');
                const isInsideChat = nodeRef.current?.contains(activeEl);

                return !isInsideChat && !isChatMenu;
            });
            setIsBlocked(isJourneyPage && externalDialogExists);
        }, 50);
    };

    document.addEventListener("focusin", checkFocus);
    document.addEventListener("mousedown", checkFocus);

    const observer = new MutationObserver(() => {
        const dialogs = document.querySelectorAll('.MuiDialog-root, .MuiDrawer-root, [role="dialog"]');
        const hasExternal = Array.from(dialogs).some(el => !nodeRef.current?.contains(el));
        if (!hasExternal || !isJourneyPage) {
            setIsBlocked(false);
        }
    });

    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
        document.removeEventListener("focusin", checkFocus);
        document.removeEventListener("mousedown", checkFocus);
        observer.disconnect();
    };
  }, [isJourneyPage]);

  const showWelcome = useMemo((): boolean => {
    return !isLoading && messages.length === 0;
  }, [messages.length, isLoading]);

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        const end = suggestion.length;
        inputRef.current.setSelectionRange(end, end);
      }
    }, 0);
  };

  const handleMaximize = useCallback(() => {
    setIsMinimized(false);
  }, [setIsMinimized]);

  const closeMinimizedIcon = () => {
    setIsMinimized(false);
    onClose();
  };

  const handleStop = (e: any, data: any) => {
    setDragPosition({ x: data.x, y: data.y });
  };

  if (!open) return null;

  const renderContent = () => {
    if (isInitializing) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      );
    }

    if (showWelcome) {
      return <ChatWelcomeScreen />;
    }

    return (
      <ChatMessages
        isLoading={isLoading || false}
        onSuggestionClick={handleSuggestionClick}
      />
    );
  };

  return (
    <>
        {isMinimized && (
            <MinimizedChatIcon onClick={handleMaximize} onClose={closeMinimizedIcon} />
        )}

        <Draggable
            nodeRef={nodeRef}
            position={dragPosition}
            onStop={handleStop}
            cancel=".non-draggable-input"
            bounds="body"
        >
            <Box
                ref={nodeRef}
                className={styles.chatDialog}
                sx={{
                    display: isMinimized ? "none" : "block",
                    bottom: 20,
                    left: 20,
                    marginLeft: view === "pastChats" && !historyOnRight ? "-302px" : "0",
                }}
            >
                {isBlocked && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 9999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "16px",
                            pointerEvents: "auto",
                            animation: "fadeIn 0.3s ease-out",
                        }}
                    >
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 2 }}>
                            <Box sx={{
                                width: 50, height: 50, bgcolor: "rgba(111, 71, 191, 0.15)", borderRadius: "50%",
                                display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
                                "&::after": {
                                    content: '""', position: "absolute", inset: 0, borderRadius: "50%",
                                    border: "2px solid #6f47bf", animation: "pulse 2.5s infinite",
                                },
                                "@keyframes pulse": {
                                    "0%": { transform: "scale(1)", opacity: 1 },
                                    "70%": { transform: "scale(1.6)", opacity: 0 },
                                    "100%": { transform: "scale(1)", opacity: 0 }
                                }
                            }}>
                                <Box sx={{ width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderLeft: "16px solid #6f47bf", ml: "4px" }} />
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontSize: "20px", fontWeight: 900, color: "#6f47bf", textShadow: "0 0 10px white" }}>Editing Node</Typography>
                                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#4b5563", textShadow: "0 0 8px white" }}>Close the node to resume the chat</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                <Box
                    className={styles.chatPanelsWrapper}
                    sx={{
                        flexDirection: historyOnRight ? "row-reverse" : "row",
                        filter: isBlocked ? "blur(4px) grayscale(0.5)" : "none",
                        transition: "filter 0.3s ease",
                    }}
                >
                    {view === "pastChats" && (
                        <Paper className={styles.pastChatsPaper}>
                            <Suspense>
                                <ChatPastSessions />
                            </Suspense>
                        </Paper>
                    )}
                    <Box className={styles.mainChatWrapper}>
                        <Box className={styles.floatingActions}>
                            <Suspense>
                                <ChatHeader onClose={onClose} />
                            </Suspense>
                        </Box>
                        <Paper className={styles.paper}>
                            <Suspense>
                                <Box
                                    className={styles.content}
                                    sx={{
                                        flexGrow: 1,
                                        overflowY: "auto",
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        // zIndex: 1,
                                        // position: "relative",
                                    }}
                                >
                                    {renderContent()}
                                </Box>
                                <div className={styles.gradientOverlay} />
                                <Box
                                    sx={{ zIndex: 11, padding: "0 16px 16px 16px" }}
                                    className="non-draggable-input"
                                >
                                    <ChatInput
                                        ref={inputRef}
                                        inputValue={inputValue}
                                        setInputValue={setInputValue}
                                        isBlocked={isBlocked}
                                    />
                                </Box>
                            </Suspense>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </Draggable>
    </>
  );
};

export default memo(ChatDialog);
